import { useId } from 'react';
import { Popover } from '@base-ui/react/popover';
import { Columns3 } from 'lucide-react';
import { buttonVariants } from '@/components/ui/button-variants';
import { cn } from '@/lib/utils';

export interface ColumnMenuProps {
  columns: { key: string; header: string }[];
  visibleColumns: string[];
  onToggleColumn: (key: string) => void;
}

/** Popover with one checkbox per column. The last visible column cannot be hidden. */
export function ColumnMenu({ columns, visibleColumns, onToggleColumn }: ColumnMenuProps) {
  const lastVisible = visibleColumns.length <= 1;
  const hintId = useId();
  return (
    <Popover.Root>
      <Popover.Trigger className={buttonVariants({ variant: 'outline', size: 'sm' })}>
        <Columns3 aria-hidden="true" />
        Columns
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Positioner side="bottom" align="end" sideOffset={6} className="z-50">
          <Popover.Popup className="min-w-48 rounded-lg border bg-popover p-2 text-popover-foreground shadow-md outline-none">
            <Popover.Title className="px-2 py-1 text-xs font-medium text-muted-foreground">Visible columns</Popover.Title>
            <ul className="mt-1">
              {columns.map((col) => {
                const checked = visibleColumns.includes(col.key);
                const locked = checked && lastVisible;
                return (
                  <li key={col.key}>
                    <label className={cn('flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-muted has-focus-visible:ring-2 has-focus-visible:ring-ring', locked && 'opacity-60')}>
                      <input
                        type="checkbox"
                        className="size-4 accent-primary"
                        checked={checked}
                        aria-disabled={locked || undefined}
                        aria-describedby={locked ? hintId : undefined}
                        onChange={() => {
                          if (!locked) onToggleColumn(col.key);
                        }}
                      />
                      {col.header}
                    </label>
                  </li>
                );
              })}
            </ul>
            {lastVisible && <p id={hintId} className="px-2 pt-1 text-xs text-muted-foreground">At least one column must stay visible</p>}
          </Popover.Popup>
        </Popover.Positioner>
      </Popover.Portal>
    </Popover.Root>
  );
}
